import { Player } from './Player.js';
import { Enemy, Boss } from './Enemy.js';
import { Bullet } from './Bullet.js';
import { Powerup } from './Powerup.js';
import { createExplosion } from './Particle.js';
import { audioManager } from '../utils/audio.js';
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  FPS,
  LEVELS,
  POWERUP_TYPES,
  BEAN_POINTS,
  POWERUP_SPAWN_CHANCE,
  ENEMY_ROWS,
  ENEMY_COLS,
  SPECIAL_DURATION,
  CHARACTERS,
  ENEMY_TYPES
} from '../config/constants.js';

// Get explosion color based on character/enemy type
function getExplosionColor(enemy) {
  // Bosses have character data - use character-specific colors
  if (enemy.type === 'boss' && enemy.bossCharacter) {
    const charId = enemy.bossCharacter.id || enemy.bossCharacter.name?.toLowerCase();

    // Cloaks characters (enemies when playing as Migos)
    if (charId === 'bat') return '#ff0000'; // RED
    if (charId === 'scythe') return '#ff0000'; // RED
    if (charId === 'archer') return '#ff0000'; // RED
    if (charId === 'cheetah') return '#ffff00'; // YELLOW
    if (charId === 'force_wolf' || charId === 'force wolf') return '#b966ff'; // PURPLE

    // Migos characters (player-side when fighting as enemy bosses)
    if (charId === '3d_guy' || charId === '3d guy' || charId === 'white_shag' || charId === 'white shag') return '#ffffff'; // WHITE
    if (charId === 'pigtail') return '#0099ff'; // BLUE
    if (charId === 'ginger') return '#ff9944'; // ORANGE
    if (charId === 'croc') return '#ffff00'; // YELLOW
    if (charId === 'snowman') return '#ffffff'; // WHITE
    if (charId === 'frog') return '#00ff00'; // GREEN

    // Beeple special case
    if (charId === 'beeple' || charId === 'beeple_dog') return '#b966ff'; // PURPLE
  }

  // Default: pink/red for regular enemies
  return '#ff6b9d';
}

// Get hit effect color based on projectile source character
function getHitEffectColor(bullet) {
  if (!bullet.sourceCharacterId) {
    // Default colors for bullets without source tracking
    return bullet.friendly ? '#7fd0ff' : '#ff4b6a';
  }

  const charId = bullet.sourceCharacterId;

  // Bat projectiles always show red hit effect
  if (charId === 'bat') return '#ff0000'; // RED

  // Other Cloaks characters
  if (charId === 'scythe') return '#ff0000'; // RED
  if (charId === 'archer') return '#ff0000'; // RED
  if (charId === 'cheetah') return '#ffff00'; // YELLOW
  if (charId === 'force_wolf' || charId === 'force wolf') return '#b966ff'; // PURPLE

  // Migos characters
  if (charId === '3d_guy' || charId === '3d guy' || charId === 'white_shag' || charId === 'white shag') return '#ffffff'; // WHITE
  if (charId === 'pigtail') return '#0099ff'; // BLUE
  if (charId === 'ginger') return '#ff9944'; // ORANGE
  if (charId === 'croc') return '#ffff00'; // YELLOW
  if (charId === 'snowman') return '#ffffff'; // WHITE
  if (charId === 'frog') return '#00ff00'; // GREEN

  // Beeple
  if (charId === 'beeple' || charId === 'beeple_dog') return '#b966ff'; // PURPLE

  // Default: red for enemy projectiles
  return '#ff4b6a';
}

export class GameEngine {
  constructor(canvas, character) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.character = character;

    this.player = null;
    this.enemies = [];
    this.bullets = [];
    this.powerups = [];
    this.particles = [];

    // Level tracking - ALWAYS use Number() to ensure integer type
    this.level = 1; // Start at level 1 (numeric)
    this.wave = 0;
    this.currentTime = 0;
    this.lastUpdateTime = Date.now();
    this.startTime = Date.now();
    this.specialStartTime = 0;
    this.specialsUsed = 0;

    this.state = 'countdown'; // countdown, playing, wave_complete, level_complete, game_over
    this.countdownTime = 3;
    this.countdownStart = Date.now();

    this.isPaused = false;
    this.isGameOver = false;
    this.levelCompleteTriggered = false; // Prevent double-trigger of level complete
    this.bossIsDying = false; // Guard flag to prevent level completion checks during boss death
    this.bossDeathTimer = 0; // Timer for boss death animation

    // Difficulty is fixed at medium
    this.difficulty = 'medium';

    // Beeple final boss tracking
    this.beepleSpawned = false;
    this.beeple = null; // Reference to main Beeple boss
    this.beepleDogsSpawnedTotal = 0; // Track TOTAL dogs ever spawned (max 5)
    this.maxBeepleDogs = 5; // Maximum 5 dogs total (not 5 alive at once)

    // Boss faction selection tracking
    this.usedBosses = []; // Track which bosses have been used
    this.availableBosses = []; // Pool of available bosses from opposite faction

    // Initialize scrolling space background
    this.stars = [];
    this.initializeStars();

    this.initialize();
  }

  initialize() {
    // Create player
    this.player = new Player(
      this.character,
      CANVAS_WIDTH / 2,
      CANVAS_HEIGHT - 60
    );

    // Set game engine reference for special abilities
    this.player.gameEngine = this;

    // Play appropriate music for this side
    this.playLevelMusic();

    this.spawnWave();
  }

  initializeStars() {
    // Create three layers of stars for parallax effect - SUBTLE for Space Invaders feel
    const starCounts = [40, 30, 20]; // Back, middle, front layers
    const speeds = [0.01, 0.02, 0.04]; // Slower speeds for less distraction
    const sizes = [1, 1.5, 2]; // Smaller in back, larger in front
    const opacities = [0.15, 0.25, 0.35]; // Much dimmer for subtle background

    for (let layer = 0; layer < 3; layer++) {
      for (let i = 0; i < starCounts[layer]; i++) {
        this.stars.push({
          x: Math.random() * CANVAS_WIDTH,
          y: Math.random() * CANVAS_HEIGHT,
          speed: speeds[layer],
          size: sizes[layer],
          opacity: opacities[layer],
          layer: layer
        });
      }
    }
  }

  playLevelMusic() {
    // Determine music track based on character side and level
    const side = this.character.side;
    const isOddLevel = this.level % 2 === 1;

    if (side === 'cloak') {
      audioManager.playMusic(isOddLevel ? 'cloaks1' : 'cloaks2');
    } else {
      audioManager.playMusic(isOddLevel ? 'migos1' : 'migos2');
    }
  }

  clearBullets() {
    // Clear all enemy bullets on wave transitions
    this.bullets = this.bullets.filter(bullet => bullet.friendly);
  }

  spawnWave() {
    this.wave++;
    const levelConfig = LEVELS[this.level - 1];

    // Clear enemy bullets on wave transition
    this.clearBullets();

    // Check if this is a boss wave
    if (this.wave > levelConfig.waves) {
      this.spawnBoss();
    } else {
      this.spawnEnemyGrid(levelConfig);
    }
  }

  spawnEnemyGrid(levelConfig) {
    const spacing = 40;
    const startX = (CANVAS_WIDTH - (ENEMY_COLS - 1) * spacing) / 2;
    const startY = 60;

    // Use level-specific enemy types and weights if available, otherwise fallback to old system
    const enemyTypes = levelConfig.enemyTypes || this.getEnemyTypesForLevel(this.level, this.wave);
    const enemyWeights = levelConfig.enemyWeights;

    for (let row = 0; row < ENEMY_ROWS; row++) {
      for (let col = 0; col < ENEMY_COLS; col++) {
        const x = startX + col * spacing;
        const y = startY + row * spacing;

        // Pick enemy type based on weights (if provided) or random from pool
        const enemyType = this.pickEnemyType(enemyTypes, enemyWeights);
        const enemy = new Enemy(x, y, enemyType, levelConfig.enemyHealth);

        // Apply level speed multiplier (only for moving enemies)
        if (enemy.baseSpeed > 0) {
          enemy.vx *= levelConfig.enemySpeed;
        }

        this.enemies.push(enemy);
      }
    }
  }

  pickEnemyType(types, weights) {
    // If weights are provided, use weighted random selection
    if (weights && weights.length === types.length) {
      const totalWeight = weights.reduce((sum, w) => sum + w, 0);
      let random = Math.random() * totalWeight;

      for (let i = 0; i < types.length; i++) {
        random -= weights[i];
        if (random <= 0) {
          return types[i];
        }
      }
      return types[types.length - 1]; // Fallback to last type
    }

    // Otherwise, simple random selection
    return types[Math.floor(Math.random() * types.length)];
  }

  getEnemyTypesForLevel(level, wave) {
    // Fallback: Progressively introduce new enemy types as levels/waves increase
    const types = ['basic', 'bacon']; // Start with fried eggs and bacon

    if (level >= 1 && wave >= 2) types.push('egg', 'bagel');
    if (level >= 2) types.push('cappuccino', 'cookies');
    if (level >= 3) types.push('muffin', 'croissant');
    if (level >= 4) types.push('coffee_pot', 'pancakes');

    return types;
  }

  // Calculate boss difficulty score (lower = easier, higher = harder)
  calculateBossDifficulty(character) {
    // Normalize values to 0-1 range and calculate weighted difficulty
    const speedScore = character.speed / 250; // Max speed is ~240
    const fireRateScore = character.fireRate / 5; // Max fire rate is ~4.5
    const bulletSpeedScore = character.bulletSpeed / 350; // Max bullet speed is ~350

    // Weight: speed and fire rate are most important for difficulty
    return (speedScore * 0.4) + (fireRateScore * 0.4) + (bulletSpeedScore * 0.2);
  }

  spawnBoss() {
    // Get opposite faction for boss
    const playerSide = this.character.side;
    const oppositeFaction = this.getOppositeFaction(playerSide);

    // Initialize available bosses pool on first boss level
    if (this.availableBosses.length === 0) {
      // Sort bosses by difficulty (easiest first)
      const sortedBosses = [...oppositeFaction].sort((a, b) => {
        return this.calculateBossDifficulty(a) - this.calculateBossDifficulty(b);
      });
      this.availableBosses = sortedBosses;
    }

    // Level 5: Spawn 2 bosses simultaneously (final dual-boss level)
    if (this.level === 5) {
      this.spawnDualBoss(oppositeFaction);
      return;
    }

    // Levels 1-4: Spawn single boss with difficulty progression
    let bossCharacter;

    if (this.level === 1) {
      // Level 1: Always pick the easiest boss
      bossCharacter = this.availableBosses[0];
      this.availableBosses.splice(0, 1);
    } else if (this.level === 2) {
      // Level 2: Pick from the easier half (but not the easiest)
      const easyCount = Math.ceil(this.availableBosses.length / 2);
      const index = Math.floor(Math.random() * easyCount);
      bossCharacter = this.availableBosses[index];
      this.availableBosses.splice(index, 1);
    } else {
      // Levels 3-4: Pick from any remaining bosses (harder ones)
      const index = Math.floor(Math.random() * this.availableBosses.length);
      bossCharacter = this.availableBosses[index];
      this.availableBosses.splice(index, 1);
    }

    this.usedBosses.push(bossCharacter);

    // If we've used all bosses, refresh the pool (shouldn't happen with 5 levels and 6 bosses)
    if (this.availableBosses.length === 0) {
      this.availableBosses = [...oppositeFaction].sort((a, b) => {
        return this.calculateBossDifficulty(a) - this.calculateBossDifficulty(b);
      });
      this.usedBosses = [];
    }

    const boss = new Boss(CANVAS_WIDTH / 2, 80, this.level, bossCharacter);
    this.enemies.push(boss);
    this.showLevelBanner(`Level ${this.level} - Boss Wave!`);
    audioManager.playMusic('boss'); // Play boss music
    audioManager.levelComplete();
  }

  spawnDualBoss(oppositeFaction) {
    // Level 5: Spawn 2 bosses at once
    // Select 2 different bosses from the available pool
    const boss1Index = Math.floor(Math.random() * this.availableBosses.length);
    const boss1Character = this.availableBosses[boss1Index];

    // Remove first boss from pool
    this.availableBosses.splice(boss1Index, 1);

    // Select second boss from remaining pool
    const boss2Index = Math.floor(Math.random() * this.availableBosses.length);
    const boss2Character = this.availableBosses[boss2Index];

    // Create two bosses positioned left and right
    // Boss 1: Shield boss (defensive, minimal shooting, dodges)
    const boss1 = new Boss(CANVAS_WIDTH / 3, 80, this.level, boss1Character, false, true);
    // Boss 2: Aggressive boss (normal behavior)
    const boss2 = new Boss((CANVAS_WIDTH * 2) / 3, 80, this.level, boss2Character, false, false);

    // Slightly reduce HP for dual boss fight to keep it challenging but fair
    boss1.health = Math.floor(boss1.health * 0.75);
    boss1.maxHealth = boss1.health;
    boss2.health = Math.floor(boss2.health * 0.75);
    boss2.maxHealth = boss2.health;

    this.enemies.push(boss1);
    this.enemies.push(boss2);
    this.showLevelBanner(`Level ${this.level} - DUAL BOSS!`);
    audioManager.playMusic('boss');
    audioManager.levelComplete();
  }

  spawnBeeple() {
    // Collect all projectiles from all Migos and Cloaks
    const allProjectiles = [
      ...CHARACTERS.cloaks.map(c => c.projectile),
      ...CHARACTERS.migos.map(c => c.projectile)
    ];

    // Create Beeple character data
    const beepleCharacter = {
      id: 'beeple',
      name: 'Beeple',
      displayName: 'Beeple',
      sprite: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692ec5610c54e8b9bce1451f_beeple.png',
      projectile: 'https://cdn.prod.website-files.com/687d960560ee14f74b3f0f52/692aad39bc5e04cdf4e60d1b_3d_bit.png', // Default projectile
      allProjectiles: allProjectiles, // Array of all projectiles to cycle through
      fireRate: 4.5, // Fast fire rate
      bulletSpeed: 320,
      particleColor: 'gold'
    };

    const beeple = new Boss(CANVAS_WIDTH / 2, 80, 10, beepleCharacter); // Level 10 difficulty
    beeple.health = 150; // Super high health
    beeple.maxHealth = 150;
    beeple.width = 80; // Larger boss
    beeple.height = 80;
    beeple.fireRate = 400; // Fires very frequently
    beeple.points = 5000; // Huge points reward

    this.enemies.push(beeple);
    this.beepleSpawned = true;
    this.beeple = beeple; // Store reference to main Beeple
    this.showLevelBanner(`FINAL BOSS: BEEPLE!`);
    audioManager.playMusic('boss'); // Play boss music
    audioManager.levelComplete();
  }

  getOppositeFaction(playerSide) {
    // Return opposite faction characters
    return playerSide === 'cloak' ? CHARACTERS.migos : CHARACTERS.cloaks;
  }

  showLevelBanner(text) {
    const banner = document.getElementById('level-banner');
    if (banner) {
      banner.textContent = text;
      banner.style.opacity = '1';
      setTimeout(() => {
        banner.style.opacity = '0';
      }, 2000);
    }
  }

  showLevelClearedOverlay(completedLevel, nextLevel) {
    const overlay = document.getElementById('level-complete-overlay');
    const titleEl = document.getElementById('level-complete-title');
    const subtitleEl = document.getElementById('level-complete-subtitle');
    const emojiEl = document.getElementById('level-complete-emoji');
    const countdownEl = document.getElementById('level-complete-countdown');

    if (!overlay) return;

    // Update text for level cleared transition
    if (titleEl) titleEl.textContent = 'LEVEL CLEARED!';

    // Get enemy sprite from the completed level
    const levelConfig = LEVELS[completedLevel - 1];
    const enemySprites = this.getLevelEnemySprites(levelConfig);
    const randomSprite = enemySprites[Math.floor(Math.random() * enemySprites.length)];

    // Replace emoji with enemy sprite image (no background - PNG transparency)
    if (emojiEl) {
      emojiEl.innerHTML = `<img src="${randomSprite}" alt="Enemy" style="width:64px;height:64px;image-rendering:pixelated;">`;
    }

    // Bitcoin & Ethereum educational facts
    const cryptoFacts = [
      // Bitcoin facts
      'Bitcoin was created in 2009 by the pseudonymous Satoshi Nakamoto.',
      'The first Bitcoin transaction was 10,000 BTC for two pizzas in 2010.',
      'There will only ever be 21 million Bitcoin in existence.',
      'Bitcoin\'s smallest unit (0.00000001 BTC) is called a Satoshi.',
      'The Bitcoin whitepaper was published on October 31, 2008.',
      'Bitcoin uses proof-of-work consensus to secure the network.',
      'The first Bitcoin block is called the "Genesis Block".',
      'Bitcoin halving events occur roughly every 4 years.',
      'Bitcoin is often called "digital gold" due to its scarcity.',
      'The Bitcoin network processes about 7 transactions per second.',
      'Bitcoin\'s blockchain has never been hacked since its creation.',
      'Over 19 million Bitcoin have already been mined.',
      'Bitcoin mining difficulty adjusts every 2,016 blocks.',
      'The first Bitcoin exchange rate was $0.0008 per BTC in 2009.',
      'Bitcoin is legal tender in El Salvador since 2021.',
      'The Lightning Network enables faster Bitcoin transactions.',
      'Lost Bitcoin wallets hold an estimated 3-4 million BTC forever.',
      'Bitcoin inspired over 20,000 other cryptocurrencies.',
      'The term "HODL" originated from a 2013 Bitcoin forum typo.',
      'Bitcoin miners receive block rewards for validating transactions.',
      'The Genesis Block contains a message about bank bailouts.',
      'Bitcoin transactions are irreversible once confirmed.',
      'A Bitcoin node can run on a Raspberry Pi computer.',
      'Bitcoin\'s total supply cap makes it deflationary by design.',
      // Ethereum facts
      'Ethereum was proposed by Vitalik Buterin in 2013.',
      'Ethereum launched on July 30, 2015.',
      'Smart contracts were popularized by Ethereum.',
      'ETH is the native currency of the Ethereum network.',
      'Ethereum switched from proof-of-work to proof-of-stake in 2022.',
      'The Ethereum merge reduced energy consumption by ~99.95%.',
      'NFTs (Non-Fungible Tokens) exploded on Ethereum in 2021.',
      'ERC-721 is the standard for NFTs on Ethereum.',
      'ERC-20 is the standard for fungible tokens on Ethereum.',
      'Ethereum processes about 30 transactions per second.',
      'Gas fees on Ethereum fluctuate based on network demand.',
      'The Ethereum Foundation is based in Switzerland.',
      'Ethereum enables decentralized applications (dApps).',
      'Layer 2 solutions help scale Ethereum transactions.',
      'Ethereum Name Service (ENS) provides .eth domain names.',
      'DeFi (Decentralized Finance) largely runs on Ethereum.',
      'Ethereum\'s Beacon Chain launched in December 2020.',
      'Vitalik Buterin was 19 when he proposed Ethereum.',
      'Ethereum has a vibrant developer community worldwide.',
      'Staking ETH helps secure the Ethereum network.',
      'Ethereum supports Turing-complete programming.',
      'The EVM (Ethereum Virtual Machine) runs smart contracts.',
      'Ethereum\'s native programming language is Solidity.',
      'Ethereum 2.0 validators need 32 ETH to stake.',
      'Ethereum burns a portion of transaction fees since EIP-1559.'
    ];

    const randomFact = cryptoFacts[Math.floor(Math.random() * cryptoFacts.length)];

    if (subtitleEl) subtitleEl.textContent = randomFact;
    if (countdownEl) countdownEl.textContent = `Level ${nextLevel} starting...`;

    // Show overlay with blur effect on canvas
    overlay.style.display = 'block';
    overlay.style.backdropFilter = 'blur(8px)';
    overlay.style.webkitBackdropFilter = 'blur(8px)';
  }

  getLevelEnemySprites(levelConfig) {
    // Get sprites from the level's enemy types
    const sprites = [];
    if (levelConfig && levelConfig.enemyTypes) {
      for (const typeId of levelConfig.enemyTypes) {
        const enemyType = ENEMY_TYPES[typeId.toUpperCase()];
        if (enemyType && enemyType.sprite) {
          sprites.push(enemyType.sprite);
        }
      }
    }
    // Fallback to basic egg if no sprites found
    if (sprites.length === 0) {
      sprites.push(ENEMY_TYPES.BASIC.sprite);
    }
    return sprites;
  }

  hideLevelClearedOverlay() {
    const overlay = document.getElementById('level-complete-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
  }

  showLevelCompleteOverlay(stats = null) {
    const overlay = document.getElementById('level-complete-overlay');
    const countdownEl = document.getElementById('level-complete-countdown');
    const emojiEl = document.getElementById('level-complete-emoji');
    const subtitleEl = document.getElementById('level-complete-subtitle');

    if (!overlay) return;

    // Replace emoji with pixelated enemy sprite
    if (emojiEl) {
      // Get random enemy type sprite
      const enemyTypes = Object.values(ENEMY_TYPES).filter(t => t.sprite);
      const randomEnemy = enemyTypes[Math.floor(Math.random() * enemyTypes.length)];

      if (randomEnemy && randomEnemy.sprite) {
        emojiEl.innerHTML = `<img src="${randomEnemy.sprite}" style="width:64px;height:64px;image-rendering:pixelated;image-rendering:-moz-crisp-edges;image-rendering:crisp-edges;" />`;
      } else {
        // Fallback to coffee emoji
        const enemyEmojis = ['☕', '🍳', '🥓', '🥯', '🍩', '🧇'];
        const randomEmoji = enemyEmojis[Math.floor(Math.random() * enemyEmojis.length)];
        emojiEl.textContent = randomEmoji;
      }
    }

    // Update subtitle with stats if available
    if (subtitleEl && stats) {
      subtitleEl.innerHTML = `
        <div style="font-size:0.75rem;color:#ffe38f;margin-bottom:8px;">
          Time: ${stats.time}s | Hits: ${stats.hits} | Beans: ${stats.beans} | Specials: ${stats.specials}
        </div>
        <div style="font-size:0.9rem;font-weight:700;color:#90ee90;">Bonus: +${stats.bonus} points!</div>
      `;
    } else if (subtitleEl) {
      subtitleEl.textContent = 'Next level brewing...';
    }

    // Show overlay
    overlay.style.display = 'block';

    // Countdown from 3 (quicker pace)
    let countdown = 3;
    if (countdownEl) countdownEl.textContent = countdown;

    const countdownInterval = setInterval(() => {
      countdown--;
      if (countdownEl) {
        if (countdown > 0) {
          countdownEl.textContent = countdown;
        } else {
          countdownEl.textContent = 'GO!';
        }
      }
      if (countdown < 0) {
        clearInterval(countdownInterval);
      }
    }, 600); // Faster countdown (600ms per number)

    // Hide overlay after countdown completes
    setTimeout(() => {
      clearInterval(countdownInterval);
      overlay.style.display = 'none';
    }, 2000); // 3 x 600ms + 200ms buffer
  }

  getDifficultyMultiplier() {
    return 1.0; // Medium baseline
  }

  // Level teardown - clear all state before loading new level
  teardownLevel() {
    // Clear all enemies
    this.enemies = [];

    // Clear all bullets
    this.bullets = [];

    // Clear all powerups
    this.powerups = [];

    // Clear all particles (optional, they naturally decay)
    // this.particles = [];

    // Reset level complete flag
    this.levelCompleteTriggered = false;

    // Clear boss death state to prevent carryover to next level
    this.bossIsDying = false;
    this.bossDeathTimer = 0;
  }

  update(input) {
    if (this.isPaused || this.isGameOver) return;

    this.currentTime = Date.now();
    const deltaTime = this.currentTime - this.lastUpdateTime;
    this.lastUpdateTime = this.currentTime;

    // Update scrolling stars background
    this.updateStars(deltaTime);

    // Handle countdown
    if (this.state === 'countdown') {
      const elapsed = (this.currentTime - this.countdownStart) / 1000;
      this.countdownTime = Math.max(0, 3 - Math.floor(elapsed));
      if (elapsed >= 3) {
        this.state = 'playing';
        this.showLevelBanner(`Level ${this.level} - Wave ${this.wave}`);
      }
      return;
    }

    // Freeze gameplay during level transition
    if (this.state === 'level_transitioning') {
      return;
    }

    // Handle boss death timer (3.2 second delay before level completion - gives time to collect hearts)
    if (this.bossIsDying) {
      this.bossDeathTimer += deltaTime;
      // Boss death sequence complete after 3200ms (2 extra seconds to collect hearts/powerups)
      if (this.bossDeathTimer >= 3200) {
        console.log('[Boss Death] Death sequence complete (3.2s), clearing guard flag');
        this.bossIsDying = false; // Clear the guard flag
        this.bossDeathTimer = 0;
        // Level completion will be checked on next frame
      }
    }

    // Update player
    this.player.update(deltaTime, input);

    // Handle special ability
    if (input.special && this.player.activateSpecial()) {
      this.specialStartTime = this.currentTime;
      this.specialsUsed++;
      audioManager.powerup();
      setTimeout(() => {
        if (this.player) this.player.specialActive = false;
      }, SPECIAL_DURATION);
    }

    // Player firing - now uses input.fire which auto-fires on movement + space bar
    if (input.fire && this.player.canFire(this.currentTime)) {
      const bulletData = this.player.fire(this.currentTime);

      // Handle both single bullets and arrays (from spread shots)
      if (Array.isArray(bulletData)) {
        bulletData.forEach(b => this.bullets.push(new Bullet(b)));
      } else {
        this.bullets.push(new Bullet(bulletData));
      }
      audioManager.shoot();
    }

    // Update enemies
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      const enemy = this.enemies[i];

      // For bosses: calculate separation from other bosses to prevent overlapping
      let separationForce = null;
      if (enemy.type === 'boss') {
        separationForce = this.calculateBossSeparation(enemy, i);
      }

      enemy.update(deltaTime, this.player, separationForce); // Pass player and separation for behaviors

      // Check for enemy-player collision
      // Player takes damage but enemy is NOT destroyed (no ramming)
      if (this.checkCollision(enemy, this.player) && !this.player.invincible) {
        this.player.takeDamage();
        // Use character-specific collision color
        const collisionColor = getExplosionColor(enemy);
        this.particles.push(...createExplosion(this.player.x, this.player.y, 8, collisionColor));
        audioManager.hit();

        // Make player temporarily invincible to prevent multiple hits from same enemy
        this.player.invincible = true;
        setTimeout(() => {
          if (this.player) this.player.invincible = false;
        }, 1000); // 1 second invincibility after contact

        // Check for game over
        if (this.player.lives <= 0) {
          this.gameOver();
        }

        // Enemy stays alive - no ramming
      }

      // Enemy firing
      if (enemy.canFire(this.currentTime)) {
        const bulletData = enemy.fire(this.currentTime, this.player); // Pass player for aiming
        if (Array.isArray(bulletData)) {
          bulletData.forEach(b => this.bullets.push(new Bullet(b)));
        } else {
          this.bullets.push(new Bullet(bulletData));
        }
      }

      // Check if enemy reached bottom of screen
      if (enemy.y + enemy.height / 2 > CANVAS_HEIGHT - 80) {
        // Bosses don't die at bottom - reposition them back to top
        if (enemy.type === 'boss') {
          enemy.y = -enemy.height;
          enemy.x = Math.random() * (CANVAS_WIDTH - 100) + 50;
        } else {
          // Regular enemies: deal damage and remove
          this.player.takeDamage();
          this.enemies.splice(i, 1);
          audioManager.hit();

          // Check for game over
          if (this.player.lives <= 0) {
            this.gameOver();
          }
        }
      }
    }

    // Beeple dog spawning logic - max 5 TOTAL ever spawned (not 5 alive)
    if (this.beeple && this.beeple.shouldSpawnDog(this.currentTime)) {
      // Only spawn if we haven't reached the total limit yet
      if (this.beepleDogsSpawnedTotal < this.maxBeepleDogs) {
        const dogData = this.beeple.createDogSpawn();
        const dog = new Boss(dogData.x, dogData.y, dogData.level, dogData.bossCharacter, true);
        dog.isDog = true; // Mark as spawned dog
        dog.fixedProjectile = dogData.fixedProjectile; // Assign unique projectile to this dog
        this.enemies.push(dog);
        this.beepleDogsSpawnedTotal++; // Increment total count
        console.log(`Beeple Dog spawned (${this.beepleDogsSpawnedTotal}/${this.maxBeepleDogs} total)`);
      }
    }

    // Update bullets
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const bullet = this.bullets[i];
      bullet.update(deltaTime);

      if (bullet.isOffScreen(CANVAS_HEIGHT)) {
        this.bullets.splice(i, 1);
        continue;
      }

      // Collision with enemies (friendly bullets)
      if (bullet.friendly) {
        for (let j = this.enemies.length - 1; j >= 0; j--) {
          const enemy = this.enemies[j];
          if (this.checkCollision(bullet, enemy)) {
            const killed = enemy.takeDamage(bullet.damage);
            if (killed) {
              // Use enemy-specific points
              this.player.addScore(enemy.points || 100);
              // Pass enemy type to spawnPowerup for Migos bonus
              const enemyTypeForPowerup = enemy.type === 'boss' && enemy.bossCharacter ? enemy.bossCharacter : enemy.typeConfig;
              this.spawnPowerup(enemy.x, enemy.y, enemyTypeForPowerup);
              // Use character-specific explosion color
              const explosionColor = getExplosionColor(enemy);

              // Bigger explosion for bosses
              const explosionSize = enemy.type === 'boss' ? 30 : 15;
              this.particles.push(...createExplosion(enemy.x, enemy.y, explosionSize, explosionColor));

              // Track boss death - set guard flag and start death sequence
              if (enemy.type === 'boss') {
                if (!this.bossIsDying) {
                  console.log('[Boss Death] Boss killed, starting 1.2s death sequence');
                  this.bossIsDying = true; // Prevent level completion checks
                  this.bossDeathTimer = 0; // Reset death timer

                  // Spawn 2 hearts when main boss dies (levels 1-4 bosses)
                  if (!enemy.isBeeple && !enemy.isDog && !enemy.isMini) {
                    for (let h = 0; h < 2; h++) {
                      const heartX = enemy.x + (h === 0 ? -20 : 20);
                      const heartY = enemy.y;
                      this.powerups.push(new Powerup(heartX, heartY, POWERUP_TYPES.HEART));
                    }
                    console.log('[Boss Death] Spawned 2 hearts from boss death');
                  }
                } else {
                  // Multiple bosses dying - reset timer to ensure full animation for last boss
                  console.log('[Boss Death] Additional boss killed, resetting death timer');
                  this.bossDeathTimer = 0;
                }

                // Spawn 1 heart when Beeple dogs die
                if (enemy.isDog) {
                  this.powerups.push(new Powerup(enemy.x, enemy.y, POWERUP_TYPES.HEART));
                  console.log('[Boss Death] Spawned 1 heart from Beeple dog death');
                }
              }

              // Check if this was the main Beeple boss
              const wasMainBeeple = enemy.isBeeple && !enemy.isMini && !enemy.isDog;

              // Handle splitting enemies (Beeple no longer splits on death)
              const splitEnemies = enemy.onDeath();
              if (splitEnemies.length > 0) {
                splitEnemies.forEach(data => {
                  let newEnemy;
                  if (data.type === 'beeple_dog' || data.type === 'beeple_mini' || data.isMini) {
                    // Create mini Boss for spawned enemies
                    newEnemy = new Boss(data.x, data.y, data.level, data.bossCharacter, true);
                    if (data.isDog) newEnemy.isDog = true; // Mark as dog
                  } else {
                    // Regular enemy split
                    newEnemy = new Enemy(data.x, data.y, data.type, 1);
                  }
                  this.enemies.push(newEnemy);
                });
              }

              this.enemies.splice(j, 1);
              audioManager.explosion();

              // If main Beeple was defeated, trigger victory even if dogs remain
              if (wasMainBeeple) {
                this.beeple = null; // Clear reference
                // Victory will be triggered on next update when enemies check runs
              }

              // Chomp Dash special ability: heal on kill
              if (this.player.specialActive &&
                  this.player.character.specialId === 'chomp_dash' &&
                  this.player.lives < 5) {
                this.player.addLife();
              }
            } else {
              this.particles.push(...createExplosion(bullet.x, bullet.y, 5, '#7fd0ff'));
              audioManager.hit();
            }
            this.bullets.splice(i, 1);
            break;
          }
        }
      }

      // Collision with player (enemy bullets)
      if (!bullet.friendly && this.checkCollision(bullet, this.player)) {
        if (this.player.takeDamage()) {
          // Use character-specific hit effect color (e.g., bat projectiles are red)
          const hitColor = getHitEffectColor(bullet);
          this.particles.push(...createExplosion(this.player.x, this.player.y, 10, hitColor));
          audioManager.hit();

          if (this.player.lives <= 0) {
            this.gameOver();
          }
        }
        this.bullets.splice(i, 1);
      }
    }

    // Update powerups
    for (let i = this.powerups.length - 1; i >= 0; i--) {
      const powerup = this.powerups[i];
      powerup.update(deltaTime);

      if (powerup.isOffScreen(CANVAS_HEIGHT)) {
        this.powerups.splice(i, 1);
        continue;
      }

      // Collision with player
      if (this.checkCollision(powerup, this.player)) {
        this.collectPowerup(powerup);
        this.powerups.splice(i, 1);
      }
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];

      // Handle both Particle class instances and plain objects (trail particles)
      if (particle.update) {
        particle.update();
        if (particle.isDead()) {
          this.particles.splice(i, 1);
        }
      } else {
        // Plain object particle (trail) - uses deltaTime in ms
        particle.x += particle.vx * deltaTime;
        particle.y += particle.vy * deltaTime;
        particle.life -= particle.decay;
        if (particle.life <= 0) {
          this.particles.splice(i, 1);
        }
      }
    }

    // Check wave/level completion
    // Skip if boss is in death sequence - wait for death animation to complete
    if (this.enemies.length === 0 && this.state === 'playing' && !this.levelCompleteTriggered && !this.bossIsDying) {
      console.log('[Level Complete] All enemies cleared, checking completion conditions');

      // Validate level is numeric
      this.level = Number(this.level);
      if (isNaN(this.level) || this.level < 1) {
        console.error('Invalid level detected, resetting to 1');
        this.level = 1;
      }

      const levelConfig = LEVELS[this.level - 1];
      if (this.wave > levelConfig.waves) {
        console.log(`[Level Complete] Level ${this.level} complete! Transitioning...`);
        // Level complete - set flag to prevent double-trigger
        this.levelCompleteTriggered = true;

        // Safety clamp: check if level exceeds max
        if (this.level >= LEVELS.length) {
          // After level 5, spawn Beeple as final boss
          if (!this.beepleSpawned) {
            this.levelCompleteTriggered = false; // Reset for Beeple fight
            this.spawnBeeple();
            return; // Don't call victory yet
          } else {
            // Beeple defeated - Victory!
            // (Dogs may still be alive, but victory is triggered by Beeple's death)
            this.victory();
            return;
          }
        } else {
          // Simple, clean level transition with "Level Cleared" overlay
          console.log(`[Level Complete] Level ${this.level} complete! Starting level ${this.level + 1}...`);

          const completedLevel = this.level;

          // Cleanup current level
          this.teardownLevel();

          // Increment level
          this.level = Number(this.level) + 1;

          // Safety clamp
          if (this.level > LEVELS.length) {
            console.warn('Level exceeded max, capping at', LEVELS.length);
            this.level = LEVELS.length;
          }

          this.wave = 0;

          // Reset player position
          this.player.x = CANVAS_WIDTH / 2;
          this.player.y = CANVAS_HEIGHT - 60;

          // Show "Level Cleared" transition overlay
          this.state = 'level_transitioning';
          this.showLevelClearedOverlay(completedLevel, this.level);
          audioManager.levelComplete();

          // After transition, start next level (extended time to read crypto facts)
          setTimeout(() => {
            this.hideLevelClearedOverlay();
            this.state = 'countdown';
            this.countdownStart = Date.now();
            this.showLevelBanner(`Level ${this.level}`);
            this.playLevelMusic();
            this.spawnWave();
          }, 4000); // 4s for transition - gives time to read facts

          return;
        }
      }
      this.spawnWave();
    }

    this.updateHUD();
  }

  updateStars(deltaTime) {
    // Scroll stars downward for space effect
    for (let i = 0; i < this.stars.length; i++) {
      const star = this.stars[i];
      star.y += star.speed * deltaTime;

      // Wrap stars back to top when they go off screen
      if (star.y > CANVAS_HEIGHT) {
        star.y = 0;
        star.x = Math.random() * CANVAS_WIDTH;
      }
    }
  }

  spawnPowerup(x, y, enemyType = null) {
    if (Math.random() > POWERUP_SPAWN_CHANCE) return;

    // Base heart spawn chance: 32% (0.32 of the POWERUP_SPAWN_CHANCE)
    const baseHeartChance = 0.32;

    // Migos enemy type multiplier: 10% boost to heart drops
    const isMigos = enemyType && (
      enemyType.id === 'n_17763' || // Migos character IDs
      enemyType.id === 'n_10346' ||
      enemyType.id === 'n_3621' ||
      enemyType.id === 'n_9042' ||
      enemyType.id === 'n_16301' ||
      enemyType.id === 'n_13270'
    );
    const enemyTypeMultiplier = isMigos ? 1.10 : 1.00;

    // Difficulty multiplier from game engine
    const difficultyMultiplier = this.getDifficultyMultiplier();

    // Final heart spawn chance: baseChance * difficultyMultiplier * enemyTypeMultiplier
    const finalHeartChance = baseHeartChance * difficultyMultiplier * enemyTypeMultiplier;

    // Clamp to safe range [0, 1]
    const clampedHeartChance = Math.max(0, Math.min(1, finalHeartChance));

    const rand = Math.random();
    let type;

    // 10% chance for gold bean (instant special)
    if (rand < 0.10) {
      type = POWERUP_TYPES.GOLD_BEAN;
    } else if (rand < 0.10 + clampedHeartChance) {
      type = POWERUP_TYPES.HEART;
    } else {
      type = POWERUP_TYPES.BEAN;
    }

    this.powerups.push(new Powerup(x, y, type));
  }

  collectPowerup(powerup) {
    switch (powerup.type) {
      case POWERUP_TYPES.BEAN:
        // Café Refill special ability: 2x bean points
        const beanMultiplier = (this.player.specialActive &&
                                 this.player.character.specialId === 'cafe_refill') ? 2 : 1;
        this.player.addScore(BEAN_POINTS * beanMultiplier);
        break;
      case POWERUP_TYPES.GOLD_BEAN:
        // Gold bean gives instant full special charge
        // Set charge to 100 and let Player.js update() handle banking/auto-activation
        this.player.specialCharge = 100;
        break;
      case POWERUP_TYPES.HEART:
        // Frog passive ability: Double healing from hearts (2 hearts instead of 1)
        const isFrog = this.player.character.displayName === 'Frog';
        if (isFrog) {
          this.player.addLife(); // First heart
          this.player.addLife(); // Second heart (frog passive)
        } else {
          this.player.addLife(); // Normal: 1 heart
        }
        break;
      case POWERUP_TYPES.SPECIAL:
        this.player.specialCharge = 100;
        break;
    }
    audioManager.powerup();
    this.particles.push(...createExplosion(powerup.x, powerup.y, 8, '#ffe38f'));
  }

  calculateBossSeparation(boss, bossIndex) {
    // Calculate repulsion force from other bosses to prevent overlapping
    const MIN_DISTANCE = 280; // Strong separation - bosses stay far apart
    let separationX = 0;
    let separationY = 0;

    for (let i = 0; i < this.enemies.length; i++) {
      if (i === bossIndex) continue; // Skip self
      const other = this.enemies[i];
      if (other.type !== 'boss') continue; // Only separate from other bosses

      const dx = boss.x - other.x;
      const dy = boss.y - other.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If bosses are too close, apply repulsion force
      if (distance < MIN_DISTANCE && distance > 0) {
        // Very strong repulsion when closer - cubed for very aggressive separation
        const strength = Math.pow((MIN_DISTANCE - distance) / MIN_DISTANCE, 2.5);
        const normalizedDx = dx / distance;
        const normalizedDy = dy / distance;
        separationX += normalizedDx * strength;
        separationY += normalizedDy * strength;
      }
    }

    if (separationX !== 0 || separationY !== 0) {
      return { x: separationX, y: separationY };
    }
    return null;
  }

  checkCollision(obj1, obj2) {
    // Use hitbox dimensions for player (tighter collision for bullet-hell grazing)
    const obj1Width = obj1.hitboxWidth || obj1.width;
    const obj1Height = obj1.hitboxHeight || obj1.height;
    const obj2Width = obj2.hitboxWidth || obj2.width;
    const obj2Height = obj2.hitboxHeight || obj2.height;

    return (
      obj1.x - obj1Width / 2 < obj2.x + obj2Width / 2 &&
      obj1.x + obj1Width / 2 > obj2.x - obj2Width / 2 &&
      obj1.y - obj1Height / 2 < obj2.y + obj2Height / 2 &&
      obj1.y + obj1Height / 2 > obj2.y - obj2Height / 2
    );
  }

  getBackgroundColor() {
    // Neutral space palette - grays, dark blues, subtle hints only
    const colors = [
      '#0f0f18', // Level 1: Deep space purple-gray
      '#0a0f14', // Level 2: Neutral dark gray-blue (no yellow)
      '#0a1420', // Level 3: Deep blue
      '#0a0f18', // Level 4: Dark blue-gray
      '#0a140a', // Level 5+: Dark green tint (for Matrix theme)
    ];
    return colors[Math.min(this.level - 1, colors.length - 1)];
  }

  renderMatrixGrid() {
    // Matrix-style grid for Beeple level (Level 5+)
    const gridSize = 20; // Smaller mesh for higher density
    const lineWidth = 1;
    const baseOpacity = 0.15; // Base opacity

    // Animated scrolling offset - slow downward drift
    const scrollSpeed = 0.01; // Pixels per millisecond
    const offsetY = (this.currentTime * scrollSpeed) % gridSize;

    // Subtle pulsing opacity for animation
    const pulseSpeed = 0.001;
    const pulseMagnitude = 0.05;
    const opacityPulse = Math.sin(this.currentTime * pulseSpeed) * pulseMagnitude;
    const opacity = baseOpacity + opacityPulse;

    this.ctx.save();
    this.ctx.strokeStyle = '#00ff41'; // Matrix green
    this.ctx.lineWidth = lineWidth;
    this.ctx.globalAlpha = opacity;

    // Vertical lines (stationary)
    for (let x = 0; x <= CANVAS_WIDTH; x += gridSize) {
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, CANVAS_HEIGHT);
      this.ctx.stroke();
    }

    // Horizontal lines (scrolling downward)
    for (let y = -gridSize; y <= CANVAS_HEIGHT + gridSize; y += gridSize) {
      this.ctx.beginPath();
      this.ctx.moveTo(0, y + offsetY);
      this.ctx.lineTo(CANVAS_WIDTH, y + offsetY);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  render() {
    // Clear canvas with level-specific background color
    this.ctx.fillStyle = this.getBackgroundColor();
    this.ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Render Matrix grid for Beeple level (Level 5+)
    if (this.level >= 5 && this.beepleSpawned) {
      this.renderMatrixGrid();
    }

    // Render scrolling space background
    this.renderStars();

    // Render particles (background layer)
    this.particles.forEach(p => {
      if (p.render) {
        p.render(this.ctx);
      } else {
        // Plain object particle (trail)
        this.ctx.save();
        this.ctx.globalAlpha = p.life;
        this.ctx.fillStyle = p.color;
        this.ctx.fillRect(p.x, p.y, p.size, p.size);
        this.ctx.restore();
      }
    });

    // Render powerups
    this.powerups.forEach(p => p.render(this.ctx));

    // Render bullets
    this.bullets.forEach(b => b.render(this.ctx));

    // Render enemies
    this.enemies.forEach(e => e.render(this.ctx));

    // Render player
    if (this.player) {
      this.player.render(this.ctx);
    }

    // Render countdown
    if (this.state === 'countdown' && this.countdownTime > 0) {
      this.ctx.save();
      this.ctx.font = 'bold 48px Arial';
      this.ctx.fillStyle = '#7fd0ff';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(this.countdownTime, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
      this.ctx.restore();
    }
  }

  renderStars() {
    this.ctx.save();
    for (let i = 0; i < this.stars.length; i++) {
      const star = this.stars[i];
      this.ctx.globalAlpha = star.opacity;
      this.ctx.fillStyle = '#ffffff';
      this.ctx.beginPath();
      this.ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      this.ctx.fill();
    }
    this.ctx.restore();
  }

  updateHUD() {
    // Update score
    const scoreEl = document.getElementById('hud-score');
    if (scoreEl) scoreEl.textContent = this.player.score;

    // Update hearts
    const heartsEl = document.getElementById('hud-hearts');
    if (heartsEl) heartsEl.textContent = this.player.lives;

    // Update level
    const levelEl = document.getElementById('hud-level');
    if (levelEl) levelEl.textContent = this.level;

    // Update special charge
    const progressBar = document.getElementById('special-progress-bar');
    if (progressBar) {
      progressBar.style.width = `${this.player.specialCharge}%`;
    }

    const specialStatus = document.getElementById('hud-special');
    if (specialStatus) {
      if (this.player.specialActive) {
        specialStatus.textContent = 'ACTIVE!';
        specialStatus.style.color = '#7fd0ff';
      } else if (this.player.specialCharge >= 100) {
        specialStatus.textContent = 'Ready';
        specialStatus.style.color = '#ffe38f';
      } else {
        specialStatus.textContent = 'Charging';
        specialStatus.style.color = '#8fa0d6';
      }
    }

    // Special hint removed - players should just know when special is ready
    // The "Ready" status text and meter provide sufficient feedback
  }

  gameOver() {
    this.isGameOver = true;
    this.state = 'game_over';
  }

  victory() {
    this.isGameOver = true;
    this.state = 'victory';
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
    // Reset lastUpdateTime to prevent large deltaTime jump
    this.lastUpdateTime = Date.now();
  }

  getGameStats() {
    const runTime = Math.floor((Date.now() - this.startTime) / 1000);
    return {
      score: this.player.score,
      level: this.level,
      runTime,
      specialsUsed: this.specialsUsed,
      heartsRemaining: this.player.lives
    };
  }
}
